import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { supabase } from '../supabaseClient';
import Navbar from '../sections/Navbar';
import { calculateSubscriptionState } from '../subscriptionUtils';
import { Printer } from 'lucide-react';
import { message, Spin } from 'antd';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

import DigitalCardPrint from '../components/DigitalCardPrint';
import brandLogoWhite from '../assets/logo/logo-white.png';

// --- Helper component for the premium "Other Benefits" section ---
const OtherBenefitsPremium = ({ benefits }) => {
  // Defensive check for benefits and its properties
  const discounts = benefits?.discounts ? Object.entries(benefits.discounts) : [];
  const gifts = benefits?.gifts ? Object.entries(benefits.gifts) : [];
  if (discounts.length === 0 && gifts.length === 0) return null;

  return (
    <div className="premium-benefits-tracker">
      <h3 className="premium-section-title">Other Active Benefits</h3>
      <div className="premium-other-benefits-grid">
        {discounts.map(([key, value]) => (
          <div key={key} className="premium-benefit-card discount">
            <label>{key.replace(/_/g, ' ')}</label>
            <span className="premium-benefit-value">{value.total} OFF</span>
          </div>
        ))}
        {gifts.map(([key, value]) => {
          const isRedeemed = value.redeemed >= 1;
          return (
            <div key={key} className="premium-benefit-card gift">
              <label>{key.replace(/_/g, ' ')}</label>
              <span className={`premium-benefit-status ${isRedeemed ? 'redeemed' : 'available'}`}>
                {isRedeemed ? 'Claimed' : 'Available'}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// --- Main Component ---
const MembershipViewScreen = () => {
  const [searchParams] = useSearchParams();
  const [membership, setMembership] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const printRef = useRef(null);

  // useEffect for data fetching and real-time updates
  useEffect(() => {
    const data = searchParams.get('data');
    if (!data) {
      setMembership(false);
      return;
    }
    let channel;
    try {
      const decodedId = JSON.parse(atob(data)).id;
      const fetchSubscription = async () => {
        const { data: subscription, error } = await supabase.from('subscriptions').select('*').eq('id', decodedId).single();
        setMembership(error ? false : subscription);
      };
      fetchSubscription();
      channel = supabase.channel(`subscription_changes_${decodedId}`)
        .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'subscriptions', filter: `id=eq.${decodedId}` },
          (payload) => setMembership(payload.new)
        ).subscribe();
    } catch (e) { setMembership(false); }
    return () => { if (channel) supabase.removeChannel(channel) };
  }, [searchParams]);

  // Derives the subscription's current state
  const subState = useMemo(() => {
    if (!membership) return { status: 'loading' };
    return calculateSubscriptionState(membership);
  }, [membership]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-GB");
  };

  // Handles the customer-facing print action
  const handlePrintCard = async () => {
    if (!printRef.current || isPrinting) return;
    setIsPrinting(true);
    message.loading({ content: 'Generating PDF...', key: 'pdf_print' });
    try {
      const canvas = await html2canvas(printRef.current, { backgroundColor: null, scale: 2 });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a7' });
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`Grams-Digital-Card-${membership.name.replace(/\s/g, '-')}.pdf`);
      message.success({ content: 'PDF Generated!', key: 'pdf_print', duration: 2 });
    } catch (error) { message.error({ content: 'Could not generate the PDF.', key: 'pdf_print', duration: 2 }); }
    setIsPrinting(false);
  };

  // --- Render Logic ---
  if (membership === null) {
    return <div className="premium-membership-page"><h2 className="page-status-message">Verifying Pass...</h2></div>;
  }
  if (membership === false) {
    return (
      <div className="premium-membership-page">
        <div className="not-found-content">
          <h2>Pass Not Found</h2>
          <p>The link seems to be invalid or expired. Please check the URL or contact us.</p>
          <Link to="/" className="back-to-menu-link">Return to Home</Link>
        </div>
      </div>
    );
  }
  
  const isPending = membership.status === 'pending';
  const isCardHolder = subState.status === 'active' || subState.status === 'paused';

  return (
    <>
      <Navbar />
      <div className="pass-page">
        <div className="pass-container">
          
          <div className="pass-intro">
            <h2>Welcome back, {membership.name}.</h2>
            <p>This is your digital pass. Present the QR code to our barista to redeem your benefits.</p>
          </div>

          <div className="pass-card">
            <div className="pass-card__header">
              <h1>Grams Subscription Card</h1>
              <div className="pass-card__qr">
                <QRCodeSVG 
                  value={window.location.href} 
                  size={64} 
                  bgColor="transparent"
                  fgColor="#FFFFFF"
                />
              </div>
            </div>
            <div className="pass-card__body">
              <h2>{membership.plan} Plan</h2>
              {isPending ? (
                <div className="pass-card__status-text">
                  <h3>Activation Pending</h3>
                  <p>Please visit us in-store to complete payment and activate your subscription.</p>
                </div>
              ) : (
                <div className="pass-card__dates">
                  <p><span>Start Date:</span> {formatDate(membership.start)}</p>
                  <p><span>End Date:</span> {formatDate(membership.expiry)}</p>
                </div>
              )}
            </div>
            <div className="pass-card__footer">
              <p className="pass-card__name">{membership.name}</p>
              <img src={brandLogoWhite} alt="Grams Logo" className="pass-card__logo" />
            </div>
          </div>

          {isPending && (
            <div className="pass-status-feedback">
              <h3>Your Pass is Ready for Activation</h3>
              <p>After payment, the barista will activate your pass. You may need to refresh this page to see your benefits.</p>
              <button onClick={() => window.location.reload()} className="pass-button">Refresh Status</button>
            </div>
          )}
         {subState.status === 'expired' && (
            <div className="pass-status-feedback is-expired">
              <h3>Your subscription has ended.</h3>
              <p>We'd love to have you back! Renew to continue enjoying your benefits.</p>
              <Link to="/subscription" className="pass-button">See Our New Plans</Link>
            </div>
          )}
          {subState.status === 'canceled' && (<div className="status-feedback canceled-feedback"><h3>Subscription Canceled</h3><p>This subscription is no longer active and cannot be used.</p><Link to="/subscription" className="renew-plan-btn">View Other Plans</Link></div>)}
          
          {subState.status === 'paused' && (
            <>
              <div className="status-feedback paused-feedback">
                <h3>Your Subscription is Paused</h3>
                <p>Your benefits countdown is currently frozen. Please visit us in-store to resume your plan.</p>
              </div>
              <div className="premium-benefits-container">
                <h3 className="premium-section-title">Your Frozen Benefits</h3>
                <div className="premium-weeks-grid is-frozen">
                  {membership.benefits.weeks.map((week) => {
                    const isCompleted = week.week < subState.currentWeek;
                    const statusClass = isCompleted ? 'completed' : 'locked';
                    return (<div key={week.week} className={`premium-week-card ${statusClass}`}>...</div>);
                  })}
                </div>
                <OtherBenefitsPremium benefits={membership.benefits} />
              </div>
            </>
          )}

          {subState.status === 'active' && (
            <div className="premium-benefits-container">
              {subState.isSunday && (<div className="premium-status-feedback sunday">...</div>)}
              <h3 className="premium-section-title">Your Weekly Benefits</h3>
              <div className="premium-weeks-grid">
                {membership.benefits?.weeks?.map((week) => {
                  const isActive = week.week === subState.currentWeek;
                  const isLocked = week.week > subState.currentWeek;
                  const isCompleted = week.week < subState.currentWeek;
                  let statusClass = isActive ? 'active' : isLocked ? 'locked' : 'completed';
                  if (subState.currentDayInWeek === 0) statusClass = 'locked';
                  return (
                    <div key={week.week} className={`premium-week-card ${statusClass}`}>
                      <div className="premium-week-header">
                        <h4>Week {week.week}</h4>
                        <span className="premium-week-status">{isActive && subState.currentDayInWeek > 0 ? 'In Progress' : isLocked ? 'Locked' : 'Completed'}</span>
                      </div>
                      <div className="premium-week-body">
                        <div className="premium-benefit-row"><span>Drinks</span><span>{week.drinks.redeemed} / {week.drinks.total}</span></div>
                        <div className="premium-benefit-row"><span>Breakfasts</span><span>{week.breakfasts.redeemed} / {week.breakfasts.total}</span></div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <OtherBenefitsPremium benefits={membership.benefits} />
            </div>
          )}

          
          <div className="store-info-footer">
            <h4>Redeemable at Grams Specialty Coffee</h4>
            <p>CF27+GCX, Agadir 80000</p>
            <a href="https://maps.app.goo.gl/1mcTEr4sa3hUkrFF8" target="_blank" rel="noopener noreferrer" className="get-directions-btn">Get Directions</a>
          </div>
        </div>

        <div style={{ position: 'absolute', left: '-9999px', top: 0, width: '1063px', height: '591px' }}>
          <DigitalCardPrint ref={printRef} passData={membership} />
        </div>
      </div>
    </>
  );
};

export default MembershipViewScreen;